import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  buildMigratePayload,
  createLegacyTransferUrl,
  createMainAppMigrateUrl,
  LEGACY_TRANSFER_TARGET_URL,
  MAIN_APP_MIGRATE_TARGET_URL,
} from '../utils/migrate';
import { markMigrationHandoffStarted } from '../utils/migrationNotice';
import { trackEvent } from '../utils/analytics';
import { createPortfolioSnapshot } from '../utils/portfolioSnapshot';

const navigateTo = (url) => window.location.assign(url);

/** Prepare and start either supported one-click migration handoff. */
export function useMigrationHandoff(navigate = navigateTo) {
  const [migratingTarget, setMigratingTarget] = useState(null);
  const [error, setError] = useState('');
  const portfolioReducer = useSelector((state) => state.portfolioReducer);
  const backupReducer = useSelector((state) => state.backupReducer);
  const settingsReducer = useSelector((state) => state.settingsReducer);
  const transferState = useMemo(
    () => ({ portfolioReducer, backupReducer, settingsReducer }),
    [backupReducer, portfolioReducer, settingsReducer]
  );
  const portfolio = useMemo(
    () => (Array.isArray(portfolioReducer?.portfolio) ? portfolioReducer.portfolio : []),
    [portfolioReducer]
  );
  const hasPortfolio = portfolio.length > 0;
  const portfolioSnapshot = useMemo(() => createPortfolioSnapshot(portfolio), [portfolio]);

  const continueTo = useCallback(
    (url, target) => {
      trackEvent('Migration: Handoff', {
        target,
        hasPortfolio,
        holdingCount: portfolioSnapshot.holdingCount,
        dividendCount: portfolioSnapshot.dividendCount,
      });
      markMigrationHandoffStarted(target);
      navigate(url);
    },
    [hasPortfolio, navigate, portfolioSnapshot]
  );

  const migrateLegacy = useCallback(() => {
    if (migratingTarget) return;
    setError('');
    setMigratingTarget('legacy');
    try {
      continueTo(
        hasPortfolio ? createLegacyTransferUrl(transferState) : LEGACY_TRANSFER_TARGET_URL,
        'legacy'
      );
    } catch {
      setError('Flytten kunde inte startas. Försök igen.');
      setMigratingTarget(null);
    }
  }, [continueTo, hasPortfolio, migratingTarget, transferState]);

  const migrateMain = useCallback(async () => {
    if (migratingTarget) return;
    setError('');
    setMigratingTarget('main');
    try {
      const url = hasPortfolio
        ? createMainAppMigrateUrl(await buildMigratePayload(portfolio))
        : MAIN_APP_MIGRATE_TARGET_URL;
      continueTo(url, 'main');
    } catch {
      setError('Flytten kunde inte startas. Försök igen.');
      setMigratingTarget(null);
    }
  }, [continueTo, hasPortfolio, migratingTarget, portfolio]);

  return {
    error,
    hasPortfolio,
    migrateLegacy,
    migrateMain,
    migratingTarget,
  };
}
